/**
 * Tab / display audio capture via getDisplayMedia.
 *
 * The plan's precise wording: getDisplayMedia is a display-capture API and
 * requires `video: true`. Audio is returned only if the user ticks "Share
 * tab audio" in the picker, and only on platforms that support it (Windows
 * + ChromeOS support whole-system; macOS + Linux limit to per-tab).
 *
 * We consume only the audio track. The video track is left attached but
 * unread -- stopping it across browsers has historically been inconsistent.
 */

export type DisplayMediaSource = {
  output: AudioNode;
  stop: () => void;
  label: string;
};

export class DisplayMediaUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DisplayMediaUnavailableError';
  }
}

export class DisplayAudioMissingError extends Error {
  constructor() {
    super(
      'Selected display surface returned no audio track. ' +
        'Most common cause: the "Share tab audio" checkbox was not ticked in the picker.',
    );
    this.name = 'DisplayAudioMissingError';
  }
}

export async function startDisplayMediaAudio(
  ctx: AudioContext,
): Promise<DisplayMediaSource> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new DisplayMediaUnavailableError(
      'navigator.mediaDevices.getDisplayMedia is unavailable in this browser.',
    );
  }

  const stream: MediaStream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: {
      // We don't want the browser to mangle music for us.
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });

  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length === 0) {
    for (const track of stream.getTracks()) track.stop();
    throw new DisplayAudioMissingError();
  }

  // Build an audio-only stream so the AnalyserNode never sees the video.
  const audioOnly = new MediaStream(audioTracks);
  const source = ctx.createMediaStreamSource(audioOnly);

  return {
    output: source,
    stop: () => {
      source.disconnect();
      for (const track of stream.getTracks()) track.stop();
    },
    label: `tab audio (${audioTracks[0]?.label ?? 'unknown'})`,
  };
}
